import { useState, useEffect } from 'react';

/**
 * Hook personnalisé pour charger un bloc global par son ID
 * Inclut un cache local pour éviter les appels répétés
 * 
 * Usage:
 * const { block, loading, error } = useGlobalBlock(blockId);
 */

// Cache global partagé entre tous les composants
const globalBlocksCache = new Map();

const useGlobalBlock = (blockId) => {
  const [block, setBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!blockId) {
      setLoading(false);
      return;
    }

    const loadBlock = async () => {
      try {
        // Vérifier le cache d'abord
        if (globalBlocksCache.has(blockId)) {
          setBlock(globalBlocksCache.get(blockId));
          setLoading(false);
          return;
        }

        // Charger depuis l'API
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/global-blocks/${blockId}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Bloc global introuvable');
          }
          throw new Error('Erreur lors du chargement du bloc global');
        }

        const data = await response.json();

        // Vérifier que le bloc est actif
        if (!data.is_active) {
          throw new Error('Bloc global inactif');
        }

        // Mettre en cache
        globalBlocksCache.set(blockId, data);
        setBlock(data);
      } catch (err) {
        console.error(`Erreur chargement bloc global ${blockId}:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadBlock();
  }, [blockId]);

  return { block, loading, error };
};

/**
 * Fonction utilitaire pour vider le cache
 * Utile lors de modifications des blocs globaux en admin
 */
export const clearGlobalBlocksCache = () => {
  globalBlocksCache.clear();
};

/**
 * Hook pour charger tous les blocs globaux actifs
 * Utile pour PageWithLayout
 */
export const useAllGlobalBlocks = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadAllBlocks = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/global-blocks`
        );

        if (!response.ok) {
          throw new Error('Erreur lors du chargement des blocs globaux');
        }

        const data = await response.json();
        
        // Mettre en cache tous les blocs
        data.forEach(block => {
          globalBlocksCache.set(block.id, block);
        });

        setBlocks(data);
      } catch (err) {
        console.error('Erreur chargement blocs globaux:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAllBlocks();
  }, []);

  return { blocks, loading, error };
};

export default useGlobalBlock;
